import type { ReactNode } from 'react';
import { useApp } from '../context';
import { useAsync } from '../hooks';
import { Empty, ErrorNote, Loading, Panel, StateBadge } from '../ui';
import { latestPublished, monthKey, previousPublished, publishedMonths } from '../../lib/periods';
import { dateLabel, deltaLabel, money, monthLabel, naira, nairaShort, shortMonthLabel } from '../../lib/format';
import { navigate } from '../../lib/route';
import { balanceAsOf, type CategoryMonth } from '../../data/types';
import { DecisionPanels } from '../Decisions';
import { LedgerStrip } from '../ledger';

const QUESTIONS = [
  { id: 'changed', text: 'What changed in this month?' },
  { id: 'lifestyle', text: 'Where did lifestyle spending go?' },
  { id: 'investing', text: 'Am I investing on target?' },
  { id: 'liquidity', text: 'How much cash is recorded?' },
] as const;

export function Insights({ month: routeMonth, question }: { month?: string; question?: string }) {
  const { repo, base } = useApp();
  const published = publishedMonths(base.months);
  const month = (routeMonth && published.find((m) => monthKey(m.month_start) === routeMonth)) || latestPublished(base.months);
  const prior = month ? previousPublished(base.months, month.month_start) : null;
  const cats = useAsync(() => (month ? repo.categoryMonths(month.id) : Promise.resolve([] as CategoryMonth[])), [month?.id]);
  const priorCats = useAsync(() => (prior ? repo.categoryMonths(prior.id) : Promise.resolve([] as CategoryMonth[])), [prior?.id]);

  if (!month) {
    return <Panel><Empty>Insights start once a month is published. Close your first month to see them.</Empty></Panel>;
  }
  const key = monthKey(month.month_start);
  const priorBy = new Map((priorCats.data ?? []).map((c) => [c.category_id, Number(c.actual_amount ?? 0)]));
  const lifestyle = (cats.data ?? []).filter((c) => c.category_type === 'lifestyle');
  const moves = lifestyle
    .filter((c) => priorBy.has(c.category_id))
    .map((c) => ({ c, d: Number(c.actual_amount ?? 0) - priorBy.get(c.category_id)! }))
    .sort((a, b) => Math.abs(b.d) - Math.abs(a.d));
  const spendLink = (name?: string) => () => navigate({ section: 'spend', month: key, category: name });
  const ask = (id: string) => navigate({ section: 'insights', month: key, question: id });

  const answer = (): ReactNode => {
    switch (question) {
      case 'changed': {
        if (!prior) return <p>No earlier published month to compare with.</p>;
        const line = (label: string, a: number | null, b: number | null) => {
          const d = deltaLabel(a, b);
          return <li key={label}>{label}: {nairaShort(a)} {d ? `(${d.text} than ${shortMonthLabel(prior.month_start)})` : ''}</li>;
        };
        return (
          <>
            <ul>
              {line('Personal spend', month.personal_spend_actual, prior.personal_spend_actual)}
              {line('Lifestyle', month.lifestyle_spend_actual, prior.lifestyle_spend_actual)}
              {line('Invested', month.investment_actual, prior.investment_actual)}
            </ul>
            {moves.slice(0, 3).length > 0 && <p>Largest category moves: {moves.slice(0, 3).map(({ c, d }) => `${c.name} ${d >= 0 ? '+' : '−'}${nairaShort(Math.abs(d))}`).join(', ')}.</p>}
            <button className="link" onClick={spendLink()}>View evidence →</button>
          </>
        );
      }
      case 'lifestyle': {
        const total = lifestyle.reduce((s, c) => s + Number(c.actual_amount ?? 0), 0);
        const top = [...lifestyle].sort((a, b) => Number(b.actual_amount ?? 0) - Number(a.actual_amount ?? 0)).slice(0, 3);
        if (!total) return <p>No lifestyle spending recorded for {monthLabel(month.month_start)}.</p>;
        return (
          <>
            <ul>{top.map((c) => <li key={c.category_id}>{c.name}: {naira(c.actual_amount)} ({Math.round((Number(c.actual_amount ?? 0) / total) * 100)}%)</li>)}</ul>
            <p>Lifestyle total {naira(total)} against a ceiling of {naira(month.lifestyle_spend_cap)}.</p>
            {top[0] && <button className="link" onClick={spendLink(top[0].name)}>View {top[0].name} transactions →</button>}
          </>
        );
      }
      case 'investing': {
        const last3 = published.filter((m) => m.month_start <= month.month_start).slice(0, 3);
        return (
          <>
            <ul>{last3.map((m) => {
              const ok = m.investment_target !== null && Number(m.investment_actual ?? 0) >= m.investment_target;
              return <li key={m.id}>{monthLabel(m.month_start)}: {naira(m.investment_actual)} of {naira(m.investment_target)} {m.investment_target !== null ? (ok ? '· met' : '· short') : ''}</li>;
            })}</ul>
            <button className="link" onClick={spendLink('Investments')}>View investment transactions →</button>
          </>
        );
      }
      case 'liquidity': {
        if (!base.accounts.length) return <p>No account balances are recorded.</p>;
        return (
          <>
            <ul>{base.accounts.map((a) => <li key={a.id}>{a.name}: {money(a.current_balance, a.currency)} (as of {dateLabel(balanceAsOf(a))})</li>)}</ul>
            <p>These are last recorded balances, not live figures.</p>
            <button className="link" onClick={() => navigate({ section: 'wealth' })}>Open Wealth →</button>
          </>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="stack">
      <Panel className="period-bar">
        <h2>{monthLabel(month.month_start)} <StateBadge state="Published Actuals">Sealed</StateBadge></h2>
        {published.length > 1 && (
          <LedgerStrip months={published} openStart={undefined} selected={key}
            onSelect={(k) => navigate({ section: 'insights', month: k, question })} label="Sealed months" />
        )}
        <nav className="questions" aria-label="Questions">
          {QUESTIONS.map((q) => (
            <button key={q.id} className={`question ${question === q.id ? 'active' : ''}`} aria-pressed={question === q.id}
              onClick={() => (question === q.id ? navigate({ section: 'insights', month: key }) : ask(q.id))}>{q.text}</button>
          ))}
        </nav>
      </Panel>

      {question && QUESTIONS.some((q) => q.id === question) && (
        <Panel title={QUESTIONS.find((q) => q.id === question)!.text} className="answer">
          {answer()}
        </Panel>
      )}

      <DecisionPanels month={month} />

      <button className="secondary pack-link" onClick={() => navigate({ section: 'report', month: key })}>Open the {monthLabel(month.month_start)} close pack</button>
    </div>
  );
}
